/**
 * W-5 品类详情(WScreen-5 category-detail.md)。L1 专题流(cursor)+ L2 纯前端过滤。
 * L2 切换不 refetch、严格 === 过滤;空页自救(<6 且有下一页→补拉);L1 切换重置 L2;
 * 总数为 L1 口径(不随 L2 变)。五态齐全。
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCategories, useProductFeed } from '@/hooks/browse';
import { findL1, l2Children } from '@/lib/categoryTree';
import { dedupeById } from '@/lib/dedupe';
import { SubcategoryChips } from '@/components/browse/SubcategoryChips';
import { ProductGrid } from '@/components/browse/ProductGrid';
import { GridSkeleton } from '@/components/browse/GridSkeleton';
import { StateBlock } from '@/components/browse/StateBlock';
import { Breadcrumb } from '@/components/browse/Breadcrumb';

export function CategoryDetailPage() {
  const { id } = useParams();
  const categoriesQ = useCategories();
  const feed = useProductFeed(id);
  const [selectedL2, setSelectedL2] = useState<string | null>(null);

  // L1 切换(路由参数变)→ 重置 L2
  useEffect(() => {
    setSelectedL2(null);
  }, [id]);

  const l1node = findL1(categoriesQ.data ?? [], id);
  const name = l1node?.name ?? '品类';
  const l2 = useMemo(() => l2Children(categoriesQ.data ?? [], id), [categoriesQ.data, id]);

  const allItems = useMemo(
    () => dedupeById((feed.data?.pages ?? []).flatMap((p) => p.items ?? [])),
    [feed.data],
  );
  const visible = useMemo(
    () =>
      selectedL2 === null
        ? allItems
        : allItems.filter((it) => it.categoryL2Id === selectedL2),
    [allItems, selectedL2],
  );
  const total = feed.data?.pages?.[0]?.total; // L1 口径,不随 L2 过滤变化

  // 空页自救:选具体 L2 后可见数 <6 且仍有下一页 → 后台补拉
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = feed;
  useEffect(() => {
    if (selectedL2 && visible.length < 6 && hasNextPage && !isFetchingNextPage && !isError) {
      fetchNextPage();
    }
  }, [selectedL2, visible.length, hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  return (
    <>
      <section className="hero">
        <div className="hero-inner">
          <div style={{ flex: 1 }}>
            <div className="sm mb-4">
              <Breadcrumb items={[{ label: '首页', to: '/' }, { label: name }]} />
            </div>
            <h1>{name}</h1>
            <p>
              {typeof total === 'number' ? `共 ${total} 个在售商品。` : ' '}
            </p>
          </div>
        </div>
      </section>

      <div className="inner section">
        <SubcategoryChips children={l2} selectedL2={selectedL2} onSelect={setSelectedL2} />

        {feed.isLoading ? (
          <GridSkeleton />
        ) : feed.isError && allItems.length === 0 ? (
          <StateBlock
            icon="⚠️"
            title="网络异常,点击重试"
            actionLabel="重试"
            onAction={() => feed.refetch()}
          />
        ) : visible.length === 0 ? (
          <StateBlock
            icon="📭"
            title={l1node ? `暂无 ${name} 商品` : '暂无商品'}
          />
        ) : (
          <ProductGrid
            items={visible}
            hasNextPage={feed.hasNextPage}
            isFetchingNextPage={feed.isFetchingNextPage}
            isPageError={feed.isError && allItems.length > 0}
            onLoadMore={() => feed.fetchNextPage()}
          />
        )}
      </div>
    </>
  );
}
